import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allComics, categoryInfoMap, categoryStats } from '@/mocks/comics';

const categoryEntries = Object.entries(categoryInfoMap);

function getCategoryJsonLd(slug: string) {
  const info = categoryInfoMap[slug];
  if (!info) return null;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://ekaichem.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': '分类浏览', 'item': 'http://ekaichem.com/category/fantasy' },
          { '@type': 'ListItem', 'position': 3, 'name': info.name },
        ],
      },
      {
        '@type': 'CollectionPage',
        'url': `http://ekaichem.com/category/${slug}`,
        'name': `${info.name} - 韩漫吧漫画分类`,
        'description': info.description,
        'inLanguage': 'zh-CN',
        'isPartOf': { '@id': 'http://ekaichem.com/#website' },
        'about': { '@type': 'Thing', 'name': info.name },
      },
    ],
  };
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'fantasy';
  const categoryInfo = categoryInfoMap[currentSlug];
  const [activeTab, setActiveTab] = useState(currentSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveTab(currentSlug);
  }, [currentSlug]);

  const filteredComics = allComics.filter((c) => c.categorySlug === currentSlug);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/fantasy' },
    { label: categoryInfo?.name || currentSlug },
  ];

  if (!categoryInfo) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl font-bold text-foreground-950">未找到该分类</h1>
            <Link to="/category/fantasy" className="mt-4 inline-block text-primary-600 hover:underline">返回分类页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(getCategoryJsonLd(currentSlug)) }}
        />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-20">
          <Breadcrumb items={breadcrumbItems} />

          <section className="py-10 md:py-14">
            <div className="flex flex-col md:flex-row md:items-center gap-6 mb-10">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-background-100 border border-background-200/70 flex items-center justify-center flex-shrink-0">
                  <span className="w-7 h-7 flex items-center justify-center text-primary-600">
                    <i className={`${categoryInfo.icon} text-2xl`}></i>
                  </span>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    {categoryInfo.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    共 {categoryInfo.count.toLocaleString()} 部漫画
                  </p>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed md:ml-auto md:max-w-md">
                {categoryInfo.description}
              </p>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-3 flex-wrap">
              {categoryEntries.map(([catSlug, info]) => (
                <Link
                  key={catSlug}
                  to={`/category/${catSlug}`}
                  onClick={() => setActiveTab(catSlug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeTab === catSlug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${info.icon} text-xs`}></i>
                  </span>
                  {info.name}
                </Link>
              ))}
            </div>
          </section>

          <section className="pb-20 md:pb-28">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredComics.length > 0 ? filteredComics.map((comic, idx) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group block"
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-100 mb-3">
                    <img
                      src={comic.image}
                      alt={comic.title}
                      title={`${comic.title} - ${comic.category}韩漫在线阅读`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      loading={idx > 5 ? 'lazy' : undefined}
                    />
                    <div className="absolute top-2 left-2 bg-foreground-900/75 text-background-50 text-xs font-medium px-2 py-1 rounded-md">
                      {comic.category}
                    </div>
                    <div className="absolute bottom-2 right-2 bg-background-50/90 text-foreground-800 text-xs font-medium px-2 py-1 rounded-md backdrop-blur-sm">
                      {comic.chapters[0].title}
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors duration-150">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="flex items-center gap-0.5 text-xs text-accent-600 font-medium">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-[10px]"></i>
                      </span>
                      {comic.rating}
                    </span>
                    <span className="text-xs text-foreground-400">{comic.status}</span>
                    <span className="text-xs text-foreground-400 ml-auto">{comic.chapters[0].updateTime}</span>
                  </div>
                </Link>
              )) : (
                <div className="col-span-full text-center py-20">
                  <p className="text-foreground-500">该分类暂无漫画</p>
                </div>
              )}
            </div>
          </section>

          <section className="pb-20 md:pb-28">
            <div className="bg-background-100 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                没找到想看的？
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto mb-8">
                韩漫吧拥有 {categoryStats.totalComics.toLocaleString()}+ 部漫画资源，每日 {categoryStats.dailyUpdates} 部更新。
                探索更多分类，发现你的下一部心头好。
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {categoryEntries.map(([catSlug, info]) => (
                  <Link
                    key={catSlug}
                    to={`/category/${catSlug}`}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      catSlug === currentSlug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    {info.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}